// components/LSSF/CounselingDetails.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdDelete, MdSearch, MdVisibility, MdEdit, MdAdd } from 'react-icons/md';
import { Link } from 'react-router-dom';

const CounselingDetails = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const limit = 20;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    educationLevel: '',
    educationInterest: ''
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      educationLevel: '',
      educationInterest: ''
    });
    setIsEditing(false);
    setSelectedItem(null);
  };

  const getCounselingData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/counseling`);
      setData(res.data.data);
      setFilteredData(res.data.data);
      const total = Math.ceil(res.data.data.length / limit);
      setTotalPages(total > 0 ? total : 1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching counseling:', err);
      toast.error('Failed to fetch counseling data');
      setLoading(false);
    }
  };

  useEffect(() => {
    getCounselingData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.filter(item => {
        return (
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.email && item.email.toLowerCase().includes(searchLower)) ||
          (item.phone && item.phone.includes(searchLower)) ||
          (item.education_level && item.education_level.toLowerCase().includes(searchLower)) ||
          (item.education_interest && item.education_interest.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedItem) {
        await axios.put(`${base_url}/api/v1/counseling/${selectedItem.id}`, formData);
        toast.success('Counseling record updated successfully');
      } else {
        await axios.post(`${base_url}/api/v1/counseling`, formData);
        toast.success('Counseling record added successfully');
      }
      resetForm();
      setShowForm(false);
      getCounselingData();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to save counseling record');
    }
  };

  const deleteItem = async (id) => {
    const choice = window.confirm('Are you sure you want to delete this counseling record?');
    if (choice) {
      try {
        await axios.delete(`${base_url}/api/v1/counseling/${id}`);
        toast.success('Counseling record deleted successfully');
        getCounselingData();
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete counseling record');
      }
    }
  };

  const viewDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const editItem = (item) => {
    setFormData({
      name: item.name,
      email: item.email,
      phone: item.phone,
      educationLevel: item.education_level || '',
      educationInterest: item.education_interest || ''
    });
    setSelectedItem(item);
    setIsEditing(true);
    setShowForm(true);
  };

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-4">
                  <h4 className="mb-0">Counseling Details</h4>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      resetForm();
                      setShowForm(!showForm);
                    }}
                  >
                    <MdAdd /> {showForm ? 'Close Form' : 'Add New'}
                  </button>
                </div>
              </div>
              <div className="card-body">
                {/* Add/Edit Form */}
                {showForm && (
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">{isEditing ? 'Edit Counseling Record' : 'Add New Counseling Record'}</h5>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Email *</label>
                            <input
                              type="email"
                              className="form-control"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Phone *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Education Level</label>
                            <select
                              className="form-control"
                              name="educationLevel"
                              value={formData.educationLevel}
                              onChange={handleChange}
                            >
                              <option value="">Select Level</option>
                              <option value="10th">10th</option>
                              <option value="12th">12th</option>
                              <option value="Graduate">Graduate</option>
                              <option value="Post Graduate">Post Graduate</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Education Interest</label>
                            <input
                              type="text"
                              className="form-control"
                              name="educationInterest"
                              value={formData.educationInterest}
                              onChange={handleChange}
                              placeholder="MBBS, BDS, BAMS etc."
                            />
                          </div>
                        </div>
                        <div className="text-end">
                          <button type="button" className="btn btn-secondary me-2" onClick={() => {
                            resetForm();
                            setShowForm(false);
                          }}>
                            Cancel
                          </button>
                          <button type="submit" className="btn btn-primary">
                            {isEditing ? 'Update' : 'Submit'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Education Level</th>
                        <th>Interest</th>
                        <th>Verified</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          {[...Array(9)].map((_, i) => (
                            <td key={i}><Skeleton count={1} /></td>
                          ))}
                        </tr>
                      ) : paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{item.name}</td>
                            <td>{item.email}</td>
                            <td>{item.phone}</td>
                            <td>{item.education_level || '-'}</td>
                            <td>{item.education_interest || '-'}</td>
                            <td>
                              <span className={`badge ${item.is_verified ? 'bg-success' : 'bg-warning'}`}>
                                {item.is_verified ? 'Verified' : 'Pending'}
                              </span>
                            </td>
                            <td>{new Date(item.created_at).toLocaleDateString()}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-info me-1"
                                onClick={() => viewDetails(item)}
                              >
                                <MdVisibility size={18} />
                              </button>
                              <button 
                                className="btn btn-sm btn-warning me-1"
                                onClick={() => editItem(item)}
                              >
                                <MdEdit size={18} />
                              </button>
                              <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteItem(item.id)}
                              >
                                <MdDelete size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger">
                          <td colSpan={9}>No counseling records found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredData.length > limit && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {showModal && selectedItem && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Counseling Details - {selectedItem.name}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Name:</strong>
                    <p>{selectedItem.name}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Email:</strong>
                    <p>{selectedItem.email}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Phone:</strong>
                    <p>{selectedItem.phone}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Education Level:</strong>
                    <p>{selectedItem.education_level || 'Not specified'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Education Interest:</strong>
                    <p>{selectedItem.education_interest || 'Not specified'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Verification Status:</strong>
                    <p>
                      <span className={`badge ${selectedItem.is_verified ? 'bg-success' : 'bg-warning'}`}>
                        {selectedItem.is_verified ? 'Verified' : 'Pending'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Submitted Date:</strong>
                    <p>{new Date(selectedItem.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default CounselingDetails;